export interface QualityCheckResult {
  numberedList: boolean;
  bulletList: boolean;
  quotes: 'pass' | 'fail' | 'not_applicable';
  paragraphVariance: boolean;
  table: boolean; // recommendation
  imageSuggestions: boolean; // recommendation
}

/** One quote per line, "quote" – name, title. Returns just the quoted texts. */
export function extractQuoteTexts(quotesField: string): string[] {
  return quotesField
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const quoted = line.match(/["”“](.+?)["”“]/);
      if (quoted) return quoted[1].trim();
      const beforeDash = line.split(/\s[–—-]\s/)[0];
      return beforeDash.trim();
    })
    .filter((q) => q.length > 0);
}

const normalize = (s: string) => s.toLowerCase().replace(/["”“'’]/g, '').replace(/\s+/g, ' ');

function bodyParagraphs(markdown: string): string[] {
  return markdown
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(
      (block) =>
        block.length > 0 &&
        !/^#{1,6}\s/.test(block) && // headings
        !/^(\d+\.|[-*])\s/m.test(block) && // lists
        !/^\|/.test(block) && // tables
        !/^\[Image suggestion:/i.test(block) &&
        !/^>/.test(block) // blockquotes
    );
}

export function runQualityChecks(markdown: string, quotes: string[]): QualityCheckResult {
  const normalizedText = normalize(markdown);

  let quotesVerdict: QualityCheckResult['quotes'];
  if (quotes.length === 0) {
    quotesVerdict = 'not_applicable';
  } else {
    quotesVerdict = quotes.every((q) => normalizedText.includes(normalize(q))) ? 'pass' : 'fail';
  }

  const paragraphs = bodyParagraphs(markdown);
  const counts = paragraphs.map((p) => p.split(/\s+/).length);
  let variance = false;
  if (counts.length >= 4) {
    const mean = counts.reduce((a, b) => a + b, 0) / counts.length;
    const sd = Math.sqrt(counts.reduce((a, c) => a + (c - mean) ** 2, 0) / counts.length);
    variance = sd >= 0.3 * mean;
  }

  return {
    numberedList: /^\s*\d+\.\s+/m.test(markdown),
    bulletList: /^\s*[-*]\s+/m.test(markdown),
    quotes: quotesVerdict,
    paragraphVariance: variance,
    table: /^\|.+\|\s*$/m.test(markdown) && /^\|[\s:|-]+\|\s*$/m.test(markdown),
    imageSuggestions: /\[Image suggestion:/i.test(markdown),
  };
}
